import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/endpoints';
import { fileUrl } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import ImageUploader from '../../components/ImageUploader';
import {
  Button, Chip, Input, Loading, Modal,
} from '../../components/Ui';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { currency } from '../../utils/format';

// The areas most showrooms walk a customer through, offered as one click each.
const SUGGESTED = ['Entrance', 'AC section', 'Refrigerators', 'TVs', 'Washing machines', 'Coolers & fans', 'Billing counter'];

/**
 * The showroom's 360° tour, built by the owner: one photo per area, then a
 * click on the photo to mark where each product stands.
 */
const ShowroomTourEditPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { partner, setPartner } = useAuth();

  const [sections, setSections] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(0);
  const [picking, setPicking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.partners.me()
      .then((r) => setSections((r.data.tour || []).map((s) => ({ ...s, hotspots: s.hotspots || [] }))))
      .catch(() => setSections([]))
      .finally(() => setLoading(false));
    api.products.mine({ limit: 100 }).then((r) => setProducts(r.data || [])).catch(() => setProducts([]));
  }, []);
  useEffect(load, [load]);

  const patch = (i, changes) => setSections((list) => list.map((s, x) => (x === i ? { ...s, ...changes } : s)));

  const addSection = (title = '') => {
    setSections((list) => [...list, { title, image: '', order: list.length, hotspots: [] }]);
    setOpen(sections.length);
  };

  // A click on the photo is a place in the shop; the product is chosen next.
  const clickPhoto = (i, e) => {
    const box = e.currentTarget.getBoundingClientRect();
    setPicking({
      section: i,
      x: +(((e.clientX - box.left) / box.width) * 100).toFixed(1),
      y: +(((e.clientY - box.top) / box.height) * 100).toFixed(1),
    });
  };

  const placeProduct = (product) => {
    const { section, x, y } = picking;
    patch(section, {
      hotspots: [...(sections[section].hotspots || []), { x, y, product: product._id, label: product.title }],
    });
    setPicking(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.partners.updateMe({
        tour: sections.filter((s) => s.image).map((s, i) => ({
          title: s.title,
          image: s.image,
          order: i,
          hotspots: (s.hotspots || []).map((h) => ({
            x: h.x, y: h.y, label: h.label, note: h.note, product: (h.product && h.product._id) || h.product,
          })),
        })),
      });
      setPartner(res.data);
      toast.success('Customers can now walk through your showroom.', 'Saved');
      navigate('/partner/business');
    } catch (err) {
      toast.error(err.message, 'Could not save');
    } finally { setSaving(false); }
  };

  if (loading) return <Loading text="Loading your showroom…" />;

  return (
    <div style={{ maxWidth: 820 }}>
      <PageHeader title="360° showroom" subtitle={(partner && partner.businessName) || 'Your tour'} back />

      <div className="card sh-sm">
        <p className="t-small">
          Add one wide photo per area of your shop. Then click the photo where a product stands and
          pick it — customers click that mark to see the price.
        </p>

        {sections.map((section, i) => (
          <div className="card" key={`section-${i}`} style={{ marginTop: 12 }}>
            <button type="button" className="row-between" style={{ width: '100%', border: 0, background: 'none', cursor: 'pointer' }}
              onClick={() => setOpen(open === i ? -1 : i)}>
              <span className="t-title">{section.title || `Area ${i + 1}`}</span>
              <span className="t-tiny">{(section.hotspots || []).length} products · {open === i ? 'Hide' : 'Edit'}</span>
            </button>

            {open === i ? (
              <div style={{ marginTop: 12 }}>
                <Input
                  label="Area name"
                  placeholder="e.g. AC section"
                  value={section.title}
                  onChange={(e) => patch(i, { title: e.target.value })}
                />
                <ImageUploader
                  label="Photo of this area"
                  hint="A wide or 360° photo works best."
                  folder="showroom"
                  multiple={false}
                  value={section.image}
                  onChange={(v) => patch(i, { image: v })}
                />

                {section.image ? (
                  <>
                    <label className="field-label">Click where a product stands</label>
                    <div className="pano-edit" onClick={(e) => clickPhoto(i, e)} role="presentation">
                      <img src={fileUrl(section.image)} alt={section.title || 'Area'} draggable={false} />
                      {(section.hotspots || []).map((h, x) => (
                        <span key={`spot-${x}`} className="pano-spot-static" style={{ left: `${h.x}%`, top: `${h.y}%` }}>🛒</span>
                      ))}
                    </div>

                    {(section.hotspots || []).map((h, x) => (
                      <div className="row-between row-line" key={`row-${x}`}>
                        <span className="t-small clamp-1">🛒 {h.label || 'Product'}</span>
                        <button type="button" className="link-btn"
                          onClick={() => patch(i, { hotspots: section.hotspots.filter((_, y) => y !== x) })}>
                          Remove
                        </button>
                      </div>
                    ))}
                  </>
                ) : null}

                <button type="button" className="link-btn" style={{ color: 'var(--danger)', marginTop: 10 }}
                  onClick={() => setSections((list) => list.filter((_, x) => x !== i))}>
                  Remove this area
                </button>
              </div>
            ) : null}
          </div>
        ))}

        <label className="field-label" style={{ marginTop: 16 }}>Add an area</label>
        <div className="row wrap" style={{ gap: 8 }}>
          {SUGGESTED.filter((t) => !sections.some((s) => s.title === t)).map((t) => (
            <Chip key={t} label={`+ ${t}`} onClick={() => addSection(t)} />
          ))}
          <Chip label="+ Something else" onClick={() => addSection('')} />
        </div>

        <div style={{ marginTop: 18 }}>
          <Button title="Save tour" loading={saving} onClick={save} />
        </div>
      </div>

      <Modal open={Boolean(picking)} onClose={() => setPicking(null)} title="Which product is here?">
        {!products.length ? (
          <div className="t-small">Add products to your catalogue first, then mark them here.</div>
        ) : products.map((p) => (
          <button type="button" key={p._id} className="row row-line" style={{ width: '100%', border: 0, background: 'none', cursor: 'pointer', gap: 12 }}
            onClick={() => placeProduct(p)}>
            {(p.images || []).length ? (
              <img src={fileUrl(p.images[0])} alt={p.title} style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }} />
            ) : null}
            <span className="grow" style={{ textAlign: 'left' }}>
              <span className="t-small" style={{ display: 'block' }}>{p.title}</span>
              <span className="t-tiny">{currency(p.price)}</span>
            </span>
          </button>
        ))}
      </Modal>
    </div>
  );
};

export default ShowroomTourEditPage;
